import React, { useState, useEffect, useCallback } from 'react';
import { useChatroom } from '../../contexts/ChatroomContext';
import './ChannelSettingsModal.css';

export function ChannelSettingsModal({ channel, onClose }) {
  const {
    participants,
    currentUserParticipant,
    loadParticipants,
    inviteParticipant,
    updateParticipantRole,
    removeParticipant,
    leaveChannelAsParticipant,
    searchInvitableUsers,
    updateChannelSettings
  } = useChatroom();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedRole, setSelectedRole] = useState('writer');

  const channelParticipants = participants[channel?.id] || [];
  const isModerator = currentUserParticipant?.role === 'moderator';

  // Load participants on mount
  useEffect(() => {
    if (channel?.id) {
      setIsLoading(true);
      loadParticipants(channel.id)
        .catch(err => setError('Failed to load participants'))
        .finally(() => setIsLoading(false));
    }
  }, [channel?.id, loadParticipants]);

  // Search users with debounce
  useEffect(() => {
    if (!searchQuery.trim() || !isModerator) {
      setSearchResults([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setIsSearching(true);
      try {
        const users = await searchInvitableUsers(channel.id, searchQuery);
        setSearchResults(users);
      } catch (err) {
        console.error('Search failed:', err);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, channel?.id, searchInvitableUsers, isModerator]);

  const handleInvite = useCallback(async (userId) => {
    try {
      await inviteParticipant(channel.id, { userId, role: selectedRole });
      setSearchQuery('');
      setSearchResults([]);
    } catch (err) {
      setError('Failed to invite user');
    }
  }, [channel?.id, inviteParticipant, selectedRole]);

  const handleRoleChange = useCallback(async (participantId, newRole) => {
    try {
      await updateParticipantRole(channel.id, participantId, newRole);
    } catch (err) {
      setError('Failed to update role');
    }
  }, [channel?.id, updateParticipantRole]);

  const handleRemove = useCallback(async (participantId) => {
    if (!window.confirm('Are you sure you want to remove this participant?')) {
      return;
    }
    try {
      await removeParticipant(channel.id, participantId);
    } catch (err) {
      setError('Failed to remove participant');
    }
  }, [channel?.id, removeParticipant]);

  const handleLeave = useCallback(async () => {
    if (!window.confirm('Are you sure you want to leave this channel?')) {
      return;
    }
    try {
      await leaveChannelAsParticipant(channel.id, currentUserParticipant?.id);
      onClose();
    } catch (err) {
      setError('Failed to leave channel');
    }
  }, [channel?.id, currentUserParticipant?.id, leaveChannelAsParticipant, onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!channel) return null;

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal-content">
        <div className="modal-header">
          <h2>#{channel.name} Settings</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        {error && (
          <div className="modal-error">
            {error}
            <button onClick={() => setError(null)}>&times;</button>
          </div>
        )}

        <div className="modal-body">
          {/* Channel Settings Section - Moderators only */}
          {isModerator && (
            <section className="settings-section">
              <h3>Channel Settings</h3>
              <div className="setting-row">
                <label className="setting-label">
                  <input
                    type="checkbox"
                    checked={channel?.settings?.trigger_on_llm_message ?? false}
                    onChange={(e) => {
                      updateChannelSettings(channel.id, {
                        trigger_on_llm_message: e.target.checked
                      }).catch(() => setError('Failed to update setting'));
                    }}
                  />
                  <span className="setting-text">
                    <strong>LLM-to-LLM Conversations</strong>
                    <span className="setting-description">
                      When enabled, LLM responses trigger new bid cycles, creating continuous AI conversations.
                      Humans can use the Pause button to interrupt.
                    </span>
                  </span>
                </label>
              </div>
            </section>
          )}

          {/* Invite Section - Moderators only */}
          {isModerator && (
            <section className="invite-section">
              <h3>Invite Participants</h3>
              <div className="invite-form">
                <input
                  type="text"
                  placeholder="Search users by username or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className="role-select"
                >
                  <option value="reader">Reader</option>
                  <option value="writer">Writer</option>
                  <option value="moderator">Moderator</option>
                </select>
              </div>
              {isSearching && <div className="search-loading">Searching...</div>}
              {searchResults.length > 0 && (
                <ul className="search-results">
                  {searchResults.map(user => (
                    <li key={user.id} className="search-result-item">
                      <span className="user-info">
                        <strong>{user.username}</strong>
                        {user.email && <span className="user-email">{user.email}</span>}
                      </span>
                      <button
                        onClick={() => handleInvite(user.id)}
                        className="invite-button"
                      >
                        Invite
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {searchQuery && !isSearching && searchResults.length === 0 && (
                <div className="no-results">No users found</div>
              )}
            </section>
          )}

          {/* Participants List */}
          <section className="participants-section">
            <h3>Participants ({channelParticipants.length})</h3>
            {isLoading ? (
              <div className="loading">Loading participants...</div>
            ) : (
              <ul className="participants-list">
                {channelParticipants.map(participant => (
                  <li key={participant.id} className="participant-item">
                    <div className="participant-info">
                      <span className="participant-name">
                        {participant.name}
                        {participant.id === currentUserParticipant?.id && (
                          <span className="you-badge">(you)</span>
                        )}
                      </span>
                      <span className={`participant-type ${participant.participant_type}`}>
                        {participant.participant_type === 'llm' ? 'AI' : 'Human'}
                      </span>
                      {!participant.invitation_accepted && (
                        <span className="pending-badge">Pending</span>
                      )}
                    </div>
                    <div className="participant-actions">
                      {isModerator && participant.id !== currentUserParticipant?.id ? (
                        <>
                          <select
                            value={participant.role}
                            onChange={(e) => handleRoleChange(participant.id, e.target.value)}
                            className="role-select-small"
                          >
                            <option value="reader">Reader</option>
                            <option value="writer">Writer</option>
                            <option value="moderator">Moderator</option>
                            {participant.participant_type === 'llm' && (
                              <option value="consultant">Consultant</option>
                            )}
                          </select>
                          <button
                            onClick={() => handleRemove(participant.id)}
                            className="remove-button"
                            title="Remove participant"
                          >
                            &times;
                          </button>
                        </>
                      ) : (
                        <span className={`role-badge ${participant.role}`}>
                          {participant.role}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Leave Channel */}
          <section className="leave-section">
            <button onClick={handleLeave} className="leave-button">
              Leave Channel
            </button>
          </section>
        </div>
      </div>
    </div>
  );
}
